<template>
  <div class="dashboard">
    <!-- 欢迎条 -->
    <div class="welcome-bar">
      <div>
        <h2 class="welcome-title">{{ isAdmin ? '超级管理员 · 平台总览' : '我的赛事 · 数据总览' }}</h2>
        <p class="welcome-sub">{{ todayText }} · 桨刻龙舟赛事数据资产平台</p>
      </div>
      <div class="welcome-badge" v-if="isAdmin">超级管理员</div>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-grid">
      <div class="stat-card" v-for="card in cards" :key="card.key" @click="card.route && $router.push(card.route)">
        <div class="stat-icon" :style="{ background: card.bg, color: card.color }">
          <a-icon :type="card.icon" />
        </div>
        <div class="stat-info">
          <div class="stat-value">{{ card.value }}</div>
          <div class="stat-label">{{ card.label }}</div>
        </div>
        <a-icon v-if="card.route" type="right" class="stat-arrow" />
      </div>
    </div>

    <!-- 中部双栏 -->
    <div class="dashboard-row">
      <!-- 注册趋势 -->
      <div class="panel trend-panel">
        <div class="panel-head">
          <span class="panel-title">注册运动员趋势</span>
          <span class="panel-tag">近 12 个月</span>
        </div>
        <div class="trend-chart">
          <div class="trend-bar" v-for="(item, i) in userTrend" :key="i" :title="`${item.month} ${item.count}人`">
            <div class="trend-bar-fill" :style="{ height: barHeight(item.count) }"></div>
            <div class="trend-bar-value">{{ item.count }}</div>
            <div class="trend-bar-label">{{ item.month }}</div>
          </div>
        </div>
      </div>

      <!-- 认证率 + 角色 -->
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">账号质量</span>
        </div>
        <div class="quality-wrap">
          <div class="ring-wrap">
            <div class="ring" :style="{ background: ringStyle }">
              <div class="ring-center">
                <div class="ring-value">{{ verifyRate }}%</div>
                <div class="ring-label">实名认证率</div>
              </div>
            </div>
          </div>
          <div class="role-list">
            <div class="role-item">
              <span class="role-dot" style="background:#4ECDC4;"></span>
              <span>注册运动员</span>
              <strong>{{ roleDistribution.athlete }}</strong>
            </div>
            <div class="role-item">
              <span class="role-dot" style="background:#F5A623;"></span>
              <span>领队</span>
              <strong>{{ roleDistribution.leader }}</strong>
            </div>
            <div class="role-item">
              <span class="role-dot" style="background:#7A8A9A;"></span>
              <span>队伍</span>
              <strong>{{ teamCount }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部双栏 -->
    <div class="dashboard-row">
      <!-- 省份分布 -->
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">注册地区分布</span>
          <span class="panel-tag">Top 8</span>
        </div>
        <div class="province-list" v-if="provinceDistribution.length">
          <div class="province-item" v-for="item in provinceDistribution" :key="item.name">
            <span class="province-name">{{ item.name }}</span>
            <div class="province-track">
              <div class="province-fill" :style="{ width: provinceWidth(item.count) }"></div>
            </div>
            <span class="province-count">{{ item.count }}</span>
          </div>
        </div>
        <div v-else class="empty-tip">暂无地区数据</div>
      </div>

      <!-- 近期赛事 -->
      <div class="panel">
        <div class="panel-head">
          <span class="panel-title">近期赛事</span>
          <a v-if="isAdmin" class="panel-more" @click="$router.push('/dragonboat/event')">全部赛事 →</a>
        </div>
        <div class="event-list" v-if="recentEvents.length">
          <div class="event-item" v-for="ev in recentEvents" :key="ev.id" @click="$router.push(`/dragonboat/event/workbench?eventId=${ev.id}`)">
            <div class="event-dot"></div>
            <div class="event-main">
              <div class="event-name">{{ ev.title }}</div>
              <div class="event-meta">{{ formatDate(ev.eventStartDate) }} · {{ ev.location || '地点待定' }}</div>
            </div>
            <a-tag :color="eventStatusColor(ev.status)">{{ eventStatusText(ev.status) }}</a-tag>
          </div>
        </div>
        <div v-else class="empty-tip">暂无赛事</div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDragonDashboardStats } from '@/api'

export default {
  name: 'Dashboard',
  data() {
    return {
      isAdmin: false,
      cards: [],
      userTrend: [],
      provinceDistribution: [],
      roleDistribution: { athlete: 0, leader: 0 },
      verifyRate: 0,
      teamCount: 0,
      recentEvents: [],
      todayText: ''
    }
  },
  created() {
    const now = new Date()
    this.todayText = `${now.getFullYear()} 年 ${now.getMonth() + 1} 月 ${now.getDate()} 日`
    this.loadStats()
  },
  methods: {
    loadStats() {
      getDragonDashboardStats().then(res => {
        const d = res.data
        this.isAdmin = d.isAdmin
        const c = d.cards || {}
        this.teamCount = c.teamCount || 0
        this.cards = [
          { key: 'event', label: '赛事', value: c.eventCount || 0, icon: 'flag', bg: '#E6F7F5', color: '#3BB7AE', route: '/dragonboat/event' },
          { key: 'team', label: '队伍', value: c.teamCount || 0, icon: 'team', bg: '#EFF4FF', color: '#5B8DEF', route: '/dragonboat/team' },
          { key: 'user', label: '注册运动员', value: c.userCount || 0, icon: 'user', bg: '#FFF3E6', color: '#F5A623', route: '/dragonboat/athlete' },
          { key: 'verified', label: '已实名', value: c.verifiedUserCount || 0, icon: 'safety-certificate', bg: '#E8F8EF', color: '#52C41A', route: '/dragonboat/athlete' },
          { key: 'reg', label: '报名总数', value: c.registrationCount || 0, icon: 'form', bg: '#F4F0FF', color: '#9254DE', route: '/dragonboat/registration' },
          { key: 'pendingReg', label: '待审核报名', value: c.pendingRegistrationCount || 0, icon: 'clock-circle', bg: '#FFF7E6', color: '#FA8C16', route: '/dragonboat/registration' },
          { key: 'pendingTeam', label: '待审核队伍', value: c.pendingTeamCount || 0, icon: 'audit', bg: '#FFF1F0', color: '#FF4D4F', route: '/dragonboat/team' },
          { key: 'published', label: '已发布成绩', value: c.publishedResultCount || 0, icon: 'trophy', bg: '#FFFBE6', color: '#FADB14', route: '/dragonboat/scoring' }
        ]
        this.userTrend = d.userTrend || []
        this.provinceDistribution = d.provinceDistribution || []
        this.roleDistribution = d.roleDistribution || { athlete: 0, leader: 0 }
        this.verifyRate = d.verifyRate || 0
        this.recentEvents = d.recentEvents || []
      })
    },
    barHeight(count) {
      const max = Math.max(...this.userTrend.map(i => i.count), 1)
      return `${Math.max(4, Math.round((count / max) * 140))}px`
    },
    provinceWidth(count) {
      const max = Math.max(...this.provinceDistribution.map(i => i.count), 1)
      return `${Math.round((count / max) * 100)}%`
    },
    formatDate(date) {
      return date ? String(date).slice(0, 10) : ''
    },
    eventStatusText(status) {
      const map = { 0: '草稿', 1: '报名中', 2: '进行中', 3: '已结束' }
      return map[status] || '未知'
    },
    eventStatusColor(status) {
      const map = { 0: 'default', 1: 'green', 2: 'blue', 3: 'purple' }
      return map[status] || 'default'
    }
  },
  computed: {
    ringStyle() {
      const rate = Math.min(this.verifyRate, 100)
      return `conic-gradient(#4ECDC4 ${rate * 3.6}deg, #E8F0F3 0deg)`
    }
  }
}
</script>

<style scoped>
.dashboard { min-height: 100vh; }

.welcome-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.welcome-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #16232F;
}

.welcome-sub {
  margin: 6px 0 0;
  color: #7A8A9A;
  font-size: 13px;
}

.welcome-badge {
  padding: 6px 16px;
  border-radius: 20px;
  background: linear-gradient(135deg, #4ECDC4, #2FBFB5);
  color: #fff;
  font-size: 13px;
  box-shadow: 0 4px 12px rgba(78, 205, 196, 0.35);
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E4EDF2;
  box-shadow: 0 2px 10px rgba(22, 35, 47, 0.04);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(22, 35, 47, 0.08);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #16232F;
  line-height: 1.2;
}

.stat-label {
  font-size: 13px;
  color: #7A8A9A;
  margin-top: 2px;
}

.stat-arrow {
  margin-left: auto;
  color: #CBDCE4;
  font-size: 12px;
}

.dashboard-row {
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.panel {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E4EDF2;
  box-shadow: 0 2px 10px rgba(22, 35, 47, 0.04);
  padding: 20px;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.panel-title {
  font-size: 15px;
  font-weight: 600;
  color: #16232F;
}

.panel-tag {
  font-size: 12px;
  color: #7A8A9A;
  background: #F4FAFA;
  padding: 2px 10px;
  border-radius: 10px;
}

.panel-more {
  font-size: 13px;
  color: #4ECDC4;
}

/* 趋势图 */
.trend-chart {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  height: 200px;
  padding-top: 10px;
}

.trend-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.trend-bar-fill {
  width: 70%;
  max-width: 28px;
  border-radius: 6px 6px 2px 2px;
  background: linear-gradient(180deg, #4ECDC4, #8BE0DA);
  transition: height 0.4s;
}

.trend-bar-value {
  font-size: 11px;
  color: #7A8A9A;
}

.trend-bar-label {
  font-size: 11px;
  color: #A8B8C2;
}

/* 质量面板 */
.quality-wrap {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 8px 0;
}

.ring-wrap { flex-shrink: 0; }

.ring {
  width: 130px;
  height: 130px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring-center {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ring-value {
  font-size: 24px;
  font-weight: 700;
  color: #16232F;
}

.ring-label {
  font-size: 12px;
  color: #7A8A9A;
  margin-top: 2px;
}

.role-list { flex: 1; }

.role-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed #E4EDF2;
  font-size: 14px;
  color: #2C3A4B;
}

.role-item:last-child { border-bottom: 0; }

.role-item strong {
  margin-left: auto;
  color: #16232F;
  font-size: 16px;
}

.role-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

/* 地区 */
.province-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.province-name {
  width: 80px;
  font-size: 13px;
  color: #2C3A4B;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.province-track {
  flex: 1;
  height: 8px;
  background: #F0F6F9;
  border-radius: 4px;
  overflow: hidden;
}

.province-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #4ECDC4, #2FBFB5);
}

.province-count {
  width: 36px;
  text-align: right;
  font-size: 13px;
  color: #7A8A9A;
}

/* 近期赛事 */
.event-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px dashed #E4EDF2;
  cursor: pointer;
}

.event-item:last-child { border-bottom: 0; }

.event-item:hover .event-name { color: #4ECDC4; }

.event-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ECDC4;
  flex-shrink: 0;
}

.event-main { flex: 1; min-width: 0; }

.event-name {
  font-size: 14px;
  font-weight: 600;
  color: #16232F;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.2s;
}

.event-meta {
  font-size: 12px;
  color: #A8B8C2;
  margin-top: 2px;
}

.empty-tip {
  text-align: center;
  color: #A8B8C2;
  padding: 40px 0;
  font-size: 13px;
}

@media (max-width: 1200px) {
  .stat-grid { grid-template-columns: repeat(2, 1fr); }
  .dashboard-row { grid-template-columns: 1fr; }
}
</style>
